/* eslint-disable react-refresh/only-export-components */
import React, { ReactNode, RefObject, useCallback, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'

import { Down, Up } from '@icon-park/react'

import loadingImgSrc from '@/assets/images/load.gif'
import { Tokens } from '@/assets/tokens'

import { Icon } from '@/components/core'

import { SelectionOption } from '../Selection.hooks'
import {
  ComponentContainer,
  Label,
  PortalSelectorContainer,
  PortalSelectorOptions,
  SelectedItem,
  SelectionContainer,
} from './Default.styles'
import { CleanButton } from '../Selection.subcomponents'

type DropdownPositionAndSize = {
  top: number
  left: number
  width: number
  maxHeight?: number
}

type Props = {
  label?: string
  labelStyle?: React.CSSProperties
  title?: string
  placeholder?: string
  options: SelectionOption[]
  onSelect: (value?: SelectionOption) => void
  selectedOption?: SelectionOption
  loading?: boolean
  required?: boolean
  children?: ReactNode
  $inverted?: boolean
  $fit?: boolean
  dropdownZIndex?: number
  disabled?: boolean
}

const DropdownMenu: React.FC<{
  options: SelectionOption[]
  onSelectOption: (option: SelectionOption) => void
  onClose: () => void
  positionAndSize: DropdownPositionAndSize
  containerRef: RefObject<HTMLDivElement>
  dropdownZIndex?: number
}> = ({ options, onSelectOption, onClose, positionAndSize, containerRef, dropdownZIndex }) => {
  const dropdownRef: RefObject<HTMLUListElement> = React.createRef()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onClose, dropdownRef, containerRef])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose])

  const style: React.CSSProperties = {
    top: `${positionAndSize.top || 0}px`,
    left: `${positionAndSize.left || 0}px`,
    width: `${positionAndSize.width || 0}px`,
    zIndex: dropdownZIndex || 1000,
    maxHeight: positionAndSize.maxHeight ? `${positionAndSize.maxHeight}px` : undefined,
  }

  return ReactDOM.createPortal(
    <PortalSelectorContainer ref={dropdownRef} style={style}>
      {options.length === 0 ?
        <PortalSelectorOptions style={{ color: Tokens.ColorTextTertiary, fontStyle: 'italic' }}>
          Nenhuma opção disponível
        </PortalSelectorOptions>
      : options.map((option) => (
          <PortalSelectorOptions key={option.value} onClick={() => onSelectOption(option)}>
            {option.text}
          </PortalSelectorOptions>
        ))
      }
    </PortalSelectorContainer>,
    document.body,
  )
}

export function DefaultSelector({
  label,
  labelStyle,
  title,
  placeholder,
  options,
  onSelect,
  selectedOption,
  loading = false,
  required = false,
  children,
  $inverted = false,
  $fit = false,
  dropdownZIndex,
  disabled = false,
}: Props): JSX.Element {
  const ref: RefObject<HTMLDivElement> = React.createRef()

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [dropdownPositionAndSize, setDropdownPositionAndSize] = useState<DropdownPositionAndSize>({
    top: 0,
    left: 0,
    width: 0,
  })

  const calculateAndSetPosition = useCallback(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const marginFromEdge = 20
      const defaultDropdownMaxHeightFromCSS = 400

      const newTop = rect.bottom + window.scrollY
      const newLeft = rect.left + window.scrollX
      let newMaxHeight = defaultDropdownMaxHeightFromCSS

      if (spaceBelow - marginFromEdge < defaultDropdownMaxHeightFromCSS) {
        newMaxHeight = Math.max(spaceBelow - marginFromEdge, 50)
      }
      setDropdownPositionAndSize({
        top: newTop,
        left: newLeft,
        width: rect.width,
        maxHeight: newMaxHeight,
      })
    }
  }, [ref])

  const handleToggleDropdown = useCallback(() => {
    if (disabled) return
    if (!isOpen) {
      calculateAndSetPosition()
    }
    setIsOpen((prev) => !prev)
  }, [isOpen, calculateAndSetPosition, disabled])

  useEffect(() => {
    if (isOpen) {
      const handleReposition = () => {
        calculateAndSetPosition()
      }
      window.addEventListener('resize', handleReposition)
      document.addEventListener('scroll', handleReposition, true)
      return () => {
        window.removeEventListener('resize', handleReposition)
        document.removeEventListener('scroll', handleReposition, true)
      }
    }
  }, [isOpen, calculateAndSetPosition])

  function handleSelection(selectedOption?: SelectionOption): void {
    if (disabled) return
    onSelect(selectedOption)
    setIsOpen(false)
  }

  const textToShow: string = selectedOption ? selectedOption.text : (placeholder ?? '')

  const $placeholderColor =
    $inverted ? Tokens.ColorWhite
    : textToShow === placeholder ? Tokens.ColorTextTertiary
    : Tokens.ColorTextPrimary

  return (
    <ComponentContainer $fit={$fit}>
      {label && (
        <Label style={labelStyle}>
          {label}
          {required && '*'}
        </Label>
      )}
      <SelectionContainer
        ref={ref}
        onClick={handleToggleDropdown}
        $inverted={$inverted}
        title={title}
        style={{
          opacity: disabled ? 0.6 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}>
        {children}
        <SelectedItem $placeholderColor={disabled ? Tokens.ColorTextTertiary : $placeholderColor}>
          {textToShow}
        </SelectedItem>
        {selectedOption && !disabled && <CleanButton onClick={() => handleSelection()} />}
        {loading ?
          <img alt="loading" src={loadingImgSrc} />
        : <Icon
            value={isOpen ? Up : Down}
            size={16}
            color={
              disabled ? Tokens.ColorTextTertiary
              : $inverted ?
                Tokens.ColorWhite
              : Tokens.ColorUIPrimary
            }
          />
        }
      </SelectionContainer>

      {isOpen && !disabled && (
        <DropdownMenu
          options={options}
          onSelectOption={handleSelection}
          onClose={() => setIsOpen(false)}
          positionAndSize={dropdownPositionAndSize}
          containerRef={ref}
          dropdownZIndex={dropdownZIndex}
        />
      )}
    </ComponentContainer>
  )
}
